import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useKeycloak } from '@react-keycloak/web';
import { Badge } from '@/components/ui/badge';
import { getTraceGroupLogsWithMcp } from './mcpApi';
import { DmsAgentRole, DmsAgentRoleLabels } from '@/constants/ai.enums';

type GroupedLogs = Record<string, TraceLog[]>;

function buildExecutionMap(logs: TraceLog[]) {
  const map = new Map<string, TraceLog>();
  logs.forEach((log) => map.set(log.executionId, log));
  return map;
}

function getBreadcrumb(log: TraceLog, map: Map<string, TraceLog>): TraceLog[] {
  const chain: TraceLog[] = [log];
  let current = log;
  while (current.parentExecutionId && map.has(current.parentExecutionId)) {
    const parent = map.get(current.parentExecutionId)!;
    chain.unshift(parent);
    current = parent;
  }
  return chain;
}

interface TraceLog {
  executionId: string;
  serviceName: string;
  status: string;
  startedAt: string;
  completedAt: string | null;
  durationMs: number | null;
  jobName: string | null;
  jobDescription: string | null;
  agentRole: DmsAgentRole | null;
  traceGroupId: string | null;
  parentExecutionId?: string;
}

// Document processing job types for identification
const DOCUMENT_JOBS = [
  'DocumentUploadIngestJob',
  'TextExtractionJob', 
  'MetadataExtractionJob',
  'DocumentVectorizationJob',
  'DocumentIngestJob'
];

const FILE_TYPE_ICONS: Record<string, string> = {
  'pdf': '📄',
  'doc': '📝',
  'docx': '📝',
  'txt': '📋',
  'rtf': '📝',
  'odt': '📝',
  'jpg': '🖼️',
  'jpeg': '🖼️',
  'png': '🖼️',
  'gif': '🖼️',
  'bmp': '🖼️',
  'tiff': '🖼️',
  'unknown': '📎'
};

const isDocumentJob = (jobName?: string) => {
  return jobName && DOCUMENT_JOBS.some(docJob => jobName.includes(docJob));
};

const getFileTypeIcon = (fileType?: string) => {
  if (!fileType) return FILE_TYPE_ICONS.unknown;
  return FILE_TYPE_ICONS[fileType.toLowerCase()] || FILE_TYPE_ICONS.unknown;
};



export default function JobTraceGroupViewer() {
  const { traceGroupId } = useParams<{ traceGroupId: string }>();
  const { keycloak } = useKeycloak();
  const [logs, setLogs] = useState<TraceLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [agentRoleFilter, setAgentRoleFilter] = useState<DmsAgentRole | ''>('');

  useEffect(() => {
    if (!traceGroupId || !keycloak.token) return;
    getTraceGroupLogsWithMcp(traceGroupId, keycloak.token).then(setLogs).finally(() => setLoading(false));
  }, [traceGroupId, keycloak.token]);

  const roleColorMap: Record<DmsAgentRole, 'success' | 'destructive' | 'secondary' | 'outline'> = {
    PLANNER: 'secondary',
    RETRIEVER: 'secondary',
    SUMMARIZER: 'success',
    CLASSIFIER: 'outline',
    PARSER: 'outline',
    POSTPROCESSOR: 'outline',
    OTHER: 'outline',
  };

  if (loading) return <p className="text-gray-400">Loading trace logs...</p>;

  const filteredLogs = agentRoleFilter
    ? logs.filter((log) => log.agentRole === agentRoleFilter)
    : logs;

  const executionMap = buildExecutionMap(filteredLogs);
  const grouped: GroupedLogs = filteredLogs.reduce((acc, log) => {
    const key = log.jobName || 'Unlabeled Job';
    acc[key] = acc[key] || [];
    acc[key].push(log);
    return acc;
  }, {} as GroupedLogs);

  // Check if this is a document processing trace group
  const documentLogs = filteredLogs.filter(log => isDocumentJob(log.jobName || ''));
  const isDocumentTraceGroup = documentLogs.length > 0;

  // Extract document metadata if this is a document trace group
  let documentInfo = null;
  if (isDocumentTraceGroup && documentLogs.length > 0) {
    // Try to find document metadata from any job in the trace
    const jobsWithMetadata = filteredLogs.filter(log => 
      log.jobDescription && log.jobDescription.includes('documentId')
    );
    
    if (jobsWithMetadata.length > 0) {
      try {
        // Try to parse document info from job description or other sources
        const sampleLog = jobsWithMetadata[0];
        // This would need to be adapted based on how document metadata is stored
        // For now, we'll extract basic info
        documentInfo = {
          documentId: 'doc-' + traceGroupId?.slice(-8),
          fileName: `Document from ${sampleLog.serviceName}`,
          fileType: 'pdf' // default, would be extracted from actual metadata
        };
      } catch (err) {
        console.log('Could not extract document metadata');
      }
    }
  }

  return (
    <div className={`p-6 ${isDocumentTraceGroup ? 'bg-gradient-to-br from-green-50 to-white dark:from-green-900/5 dark:to-gray-900' : ''}`}>
      {/* Enhanced Breadcrumb */}
      <div className="text-sm mb-4 text-gray-500 dark:text-gray-400">
        <Link to="/system-monitor" className="hover:underline text-lex-accent">
          System Monitor
        </Link>{' '}
        /{' '}
        <span className="font-medium text-gray-700 dark:text-gray-200">
          {isDocumentTraceGroup ? '📄 Document' : ''} Trace Group: {traceGroupId}
        </span>
      </div>

      {/* Enhanced Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className={`text-2xl font-bold mb-2 ${
            isDocumentTraceGroup ? 'text-green-800 dark:text-green-200' : ''
          }`}>
            {isDocumentTraceGroup && <span className="mr-2">📄</span>}
            {isDocumentTraceGroup ? 'Document Processing' : ''} Trace Group: {traceGroupId}
          </h1>
          <p className="text-gray-500 text-sm">
            {logs.length} agent executions grouped under this workflow.
          </p>
        </div>

        {/* Document Info and Chat Link */}
        {isDocumentTraceGroup && documentInfo && (
          <div className="flex items-center gap-4">
            <div className="text-right">
              <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                <span className="text-2xl">{getFileTypeIcon(documentInfo.fileType)}</span>
                <div>
                  <div className="font-medium">{documentInfo.fileName}</div>
                  <div className="text-xs">{documentInfo.fileType?.toUpperCase()}</div>
                </div>
              </div>
            </div>
            <a
              href={`/chat?documentId=${documentInfo.documentId}`}
              target="_blank"
              rel="noopener noreferrer"
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2"
            >
              💬 Jump to Chat
            </a>
          </div>
        )}
      </div>

      {/* Document Processing Summary */}
      {isDocumentTraceGroup && (
        <div className="mb-6 p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
          <h3 className="text-sm font-semibold text-green-800 dark:text-green-200 mb-3">
            📋 Document Processing Summary
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <div className="text-gray-600 dark:text-gray-400">Upload Jobs</div>
              <div className="font-semibold text-green-700 dark:text-green-300">
                {filteredLogs.filter(log => log.jobName?.includes('Upload')).length}
              </div>
            </div>
            <div>
              <div className="text-gray-600 dark:text-gray-400">Extraction Jobs</div>
              <div className="font-semibold text-green-700 dark:text-green-300">
                {filteredLogs.filter(log => log.jobName?.includes('Extraction')).length}
              </div>
            </div>
            <div>
              <div className="text-gray-600 dark:text-gray-400">Vectorization Jobs</div>
              <div className="font-semibold text-green-700 dark:text-green-300">
                {filteredLogs.filter(log => log.jobName?.includes('Vectorization')).length}
              </div>
            </div>
            <div>
              <div className="text-gray-600 dark:text-gray-400">Success Rate</div>
              <div className="font-semibold text-green-700 dark:text-green-300">
                {Math.round((filteredLogs.filter(log => log.status === 'SUCCESS').length / filteredLogs.length) * 100)}%
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="mb-4 flex items-center gap-4">
        <div className="flex items-center gap-2">
          <label htmlFor="agent-role-filter" className="text-sm text-gray-700 dark:text-gray-300">
            Filter by Agent Role:
          </label>
          <select
            id="agent-role-filter"
            value={agentRoleFilter}
            onChange={(e) => setAgentRoleFilter(e.target.value as DmsAgentRole | '')}
            className="text-sm px-2 py-1 rounded border dark:bg-gray-800 dark:text-white dark:border-gray-700"
          >
            <option value="">All Roles</option>
            {Object.values(DmsAgentRole).map((role) => (
              <option key={role} value={role}>
                {DmsAgentRoleLabels[role]}
              </option>
            ))}
          </select>
        </div>
        
        {isDocumentTraceGroup && (
          <div className="flex items-center gap-2">
            <span className="px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200 rounded text-xs font-medium">
              📄 Document Processing Pipeline
            </span>
          </div>
        )}
      </div>

      {/* Enhanced Table */}
      <div className={`overflow-x-auto shadow rounded-lg border ${
        isDocumentTraceGroup ? 'border-green-200 dark:border-green-800' : 'dark:border-gray-700'
      }`}>
        {Object.entries(grouped).map(([jobName, logsForJob]) => (
          <div key={jobName} className="mb-6">
            <h2 className={`text-xl font-semibold mb-2 px-4 py-2 flex items-center gap-2 ${
              isDocumentTraceGroup 
                ? 'bg-green-50 dark:bg-green-900/20 text-green-800 dark:text-green-200' 
                : 'bg-gray-50 dark:bg-gray-850'
            }`}>
              {isDocumentJob(jobName) && <span>📄</span>}
              {jobName}
              {isDocumentJob(jobName) && (
                <span className="text-xs px-2 py-1 bg-green-600 text-white rounded ml-2">
                  Document Job
                </span>
              )}
            </h2>
            <table className="min-w-full text-sm text-left mb-2">
              <thead className={`text-gray-700 dark:text-gray-300 ${
                isDocumentTraceGroup 
                  ? 'bg-green-100 dark:bg-green-900/30' 
                  : 'bg-gray-100 dark:bg-gray-800'
              }`}>
                <tr>
                  <th className="px-4 py-2">Breadcrumb</th>
                  <th className="px-4 py-2">Status</th>
                  <th className="px-4 py-2">Role</th>
                  <th className="px-4 py-2">Duration</th>
                  <th className="px-4 py-2">Started</th>
                  <th className="px-4 py-2">Description</th>
                  {isDocumentTraceGroup && <th className="px-4 py-2">Actions</th>}
                </tr>
              </thead>
              <tbody>
                {logsForJob.map((log) => {
                  const chain = getBreadcrumb(log, executionMap);
                  return (
                    <tr
                      key={log.executionId}
                      className={`border-t hover:bg-opacity-75 ${
                        isDocumentTraceGroup 
                          ? 'border-green-200 dark:border-green-750 hover:bg-green-50 dark:hover:bg-green-900/10' 
                          : 'border-gray-200 dark:border-gray-750 hover:bg-gray-50 dark:hover:bg-gray-800'
                      }`}
                    >
                      <td className="px-4 py-2">
                        {chain.map((node, idx) => (
                          <span key={node.executionId}>
                            <Link
                              to={`/system-monitor/job-trace/${node.executionId}`}
                              className={isDocumentTraceGroup
                                ? "text-green-600 hover:underline dark:text-green-400"
                                : "text-lex-accent hover:underline"
                              }
                            >
                              {node.serviceName}
                            </Link>
                            {idx < chain.length - 1 && ' → '}
                          </span>
                        ))}
                      </td>
                      <td className="px-4 py-2">
                        <Badge
                          variant={
                            log.status === 'SUCCESS'
                              ? 'success'
                              : log.status === 'FAIL'
                              ? 'destructive'
                              : 'secondary'
                          }
                        >
                          {log.status}
                        </Badge>
                      </td>
                      <td className="px-4 py-2">
                        {log.agentRole ? (
                          <span title={`Agent Role: ${DmsAgentRoleLabels[log.agentRole]}`}>
                            <Badge variant={roleColorMap[log.agentRole] || 'outline'}>
                              {DmsAgentRoleLabels[log.agentRole]}
                            </Badge>
                          </span>
                        ) : (
                          <span className="text-gray-400 italic">n/a</span>
                        )}
                      </td>
                      <td className="px-4 py-2">{log.durationMs ? `${log.durationMs} ms` : '—'}</td>
                      <td className="px-4 py-2">{new Date(log.startedAt).toLocaleString()}</td>
                      <td className="px-4 py-2 text-gray-700 dark:text-gray-300">{log.jobDescription || '—'}</td>
                      {isDocumentTraceGroup && (
                        <td className="px-4 py-2">
                          {documentInfo && (
                            <a
                              href={`/chat?documentId=${documentInfo.documentId}&jobId=${log.executionId}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-green-600 dark:text-green-400 hover:underline text-xs"
                            >
                              Chat
                            </a>
                          )}
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ))}
      </div>
    </div>
  );
}
